import { SectionAccordion } from '@/components/home/section-accordion';
import { SectionBadgesHorizontal } from '@/components/home/section-badges-horizontal';
import { SectionHintBubble } from '@/components/home/section-hint-bubble';
import { TemplatePicker } from '@/components/home/template-picker';
import { TranscriptionDisplay } from '@/components/home/transcription-display';
import { useIsMobile } from '@/hooks/use-mobile';
import { useSectionGuidance } from '@/hooks/use-section-guidance';
import { isActive, useTranscription } from '@/hooks/use-transcription';
import { KeepAwake } from '@capacitor-community/keep-awake';
import {
  convexQuery,
  useConvexAction,
  useConvexMutation,
} from '@convex-dev/react-query';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { Doc, Id } from 'convex/_generated/dataModel';
import { useMutation as useConvexMutationDirect } from 'convex/react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertCircleIcon,
  ArrowRight,
  ChevronLeft,
  Loader2,
  Trash2,
} from 'lucide-react';
import { parseAsBoolean, useQueryState } from 'nuqs';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '~/convex/_generated/api';
import { isDefaultReportTitle } from '~/convex/lib/types';
import { EditableReportTitle } from '../common/editable-report-title';
import { RecordingButton } from '../common/recording-button';
import { ReportStatusBadge } from '../common/report-status-badge';
import { ResponsiveContainer } from '../layout/responsive-container';
import { Button } from '../ui/button';

interface ActiveRecorderProps {
  report: Doc<'reports'>;
}

export const ActiveRecorder = ({ report }: ActiveRecorderProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const {
    status,
    previousText,
    currentSegmentText,
    finalText,
    nonFinalText,
    startTranscription,
    stopTranscription,
    error,
  } = useTranscription({ reportId: report._id });

  const { data: template } = useQuery(
    convexQuery(
      api.templates.get,
      report.templateId != null ? { id: report.templateId } : 'skip',
    ),
  );

  const guidance = useSectionGuidance({
    reportId: report._id,
    transcript: finalText,
    isRecording: isActive(status),
  });

  const isMobile = useIsMobile();
  const [isTitleEditing, setIsTitleEditing] = useState(false);
  const [autoStart, setAutoStart] = useQueryState(
    'autoStart',
    parseAsBoolean.withDefault(false),
  );

  useEffect(() => {
    if (autoStart) {
      startTranscription();
      setAutoStart(false);
    }
  }, [autoStart, setAutoStart, startTranscription]);

  useEffect(() => {
    if (error?.status) {
      toast.error(t(`home:errors.${error.status}.title`), {
        description: t(`home:errors.${error.status}.description`, {
          message: error?.message,
        }),
      });
    }
  }, [error?.status, error?.message, t]);

  const { mutate: deleteReport, isPending: isDeleting } = useMutation({
    mutationFn: useConvexMutation(api.reports.remove),
  });

  const { mutate: reportExtractData, isPending: isExtracting } = useMutation({
    mutationFn: useConvexAction(api.reportActions.extractData),
  });

  const { mutate: extractTitle } = useMutation({
    mutationFn: useConvexAction(api.sectionAnalysis.extractReportTitle),
  });

  const updateReportTemplate = useConvexMutationDirect(
    api.reports.assignReportTemplate,
  ).withOptimisticUpdate((localStore, args) => {
    const current = localStore.getQuery(api.reports.get, { id: args.id });
    if (current != null) {
      localStore.setQuery(
        api.reports.get,
        { id: args.id },
        {
          ...current,
          templateId: args.templateId,
        },
      );
    }
  });

  const handleTemplateChange = (templateId: Id<'templates'>) => {
    updateReportTemplate({
      id: report._id,
      templateId,
    });
  };

  const handleExtractData = async () => {
    if (!report.templateId) {
      toast.error(t('home:selectTemplate'));
      return;
    }

    const shouldStopTranscription =
      isActive(status) || status === 'stopping' || status === 'finalizing';
    if (shouldStopTranscription) {
      await stopTranscription();
    }

    reportExtractData(
      { reportId: report._id },
      {
        onError: (err) => {
          console.error(err);
          toast.error(t('errors:errorExtractingData'));
        },
      },
    );

    navigate(`/reports/${report._id}/processing`, {
      replace: true,
      state: { generating: true },
    });
  };

  const handleDelete = () => {
    if (isActive(status)) {
      void stopTranscription();
    }

    deleteReport(
      { id: report._id },
      {
        onSuccess: () => navigate('/'),
        onError: (err) => {
          console.error(err);
          toast.error(t('errors:errorDeletingReport'));
        },
      },
    );
  };

  // Track previous recording state to detect stop transitions
  const wasRecordingRef = useRef(false);
  const isTranscribing = isActive(status);
  const isRecorderTransitioning =
    status === 'stopping' || status === 'finalizing';

  // Auto-extract report title when recording stops and title is still default
  useEffect(() => {
    if (isTranscribing) {
      wasRecordingRef.current = true;
    } else if (wasRecordingRef.current && status === 'idle') {
      wasRecordingRef.current = false;
      if (isDefaultReportTitle(report.patientName) && finalText.length > 0) {
        extractTitle({
          reportId: report._id,
          transcript: finalText,
          locale: i18n.language,
        });
      }
    }
  }, [
    isTranscribing,
    status,
    report.patientName,
    report._id,
    finalText,
    i18n.language,
    extractTitle,
  ]);
  const transcriptionExists =
    finalText.length > 0 ||
    nonFinalText.length > 0 ||
    report.rawTranscription.length > 0;

  useEffect(() => {
    if (isTranscribing) {
      KeepAwake.keepAwake();
    } else {
      KeepAwake.allowSleep();
    }
    return () => {
      KeepAwake.allowSleep();
    };
  }, [isTranscribing]);

  return (
    <>
      <ResponsiveContainer
        scrollAwareBorder
        className="flex w-full flex-row items-center gap-2 bg-white py-4 md:pt-6">
        <Button
          className="cursor-pointer md:hidden"
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}>
          <ChevronLeft className="size-5" />
        </Button>
        <div className="flex flex-1 items-center gap-3 overflow-hidden">
          <EditableReportTitle
            reportId={report._id}
            title={report.patientName}
            onEditingChange={setIsTitleEditing}
          />
          {!(isMobile && isTitleEditing) && (
            <ReportStatusBadge status={report.status} />
          )}
        </div>
      </ResponsiveContainer>
      <ResponsiveContainer className="w-full pb-3 md:hidden">
        <TemplatePicker
          selectedTemplateId={report.templateId ?? null}
          onSelectTemplate={handleTemplateChange}
          disabled={isActive(status) || isExtracting}
        />
      </ResponsiveContainer>
      <ResponsiveContainer className="relative flex h-full min-h-0 w-full flex-col gap-4 md:flex-row">
        {/* Desktop: Vertical accordion on the left */}
        <div className="hidden md:flex md:flex-1 md:flex-col md:gap-2 md:overflow-y-auto">
          <p className="text-xs font-light text-gray-500 uppercase">
            {t('home:selectTemplate')}
          </p>
          <TemplatePicker
            selectedTemplateId={report.templateId ?? null}
            onSelectTemplate={handleTemplateChange}
            disabled={isActive(status) || isExtracting}
          />
          {template?.sections && template.sections.length > 0 ? (
            <SectionAccordion
              className="mt-2"
              sections={template.sections}
              activeSectionIndex={guidance.activeSectionIndex}
              entries={guidance.entries}
              hint={guidance.hint}
              isAnalyzing={guidance.isAnalyzing}
              onSectionClick={guidance.setActiveSection}
            />
          ) : (
            <>
              <p className="text-xs font-light text-gray-500 uppercase">
                {t('templates:sections')}
              </p>
              <div className="flex flex-col gap-2">
                {template?.sections.map((section) => (
                  <div
                    key={section.title}
                    className="flex flex-col gap-2 rounded-lg border bg-gray-100 px-4 py-2.5">
                    <p className="text-sm font-medium">{section.title}</p>
                    <p className="text-sm text-gray-500">{section.prompt}</p>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Mobile & Desktop: Main content area */}
        {!error && (
          <div className="flex h-full min-h-0 flex-1 flex-col gap-3">
            {/* Mobile: Horizontal scrollable badges at the top */}
            {template?.sections && template.sections.length > 0 && (
              <div className="md:hidden">
                <SectionBadgesHorizontal
                  sections={template.sections}
                  activeSectionIndex={guidance.activeSectionIndex}
                  entries={guidance.entries}
                  onSectionClick={guidance.setActiveSection}
                />

                {guidance.hint && (
                  <div className="mt-3">
                    <SectionHintBubble hint={guidance.hint} />
                  </div>
                )}

                {/* Mobile: Show active section details + hint below badges */}
                <AnimatePresence mode="wait">
                  {(() => {
                    const activeSection =
                      template.sections[guidance.activeSectionIndex];
                    if (!activeSection) return null;

                    const activeEntry = guidance.entries.find(
                      (e) => e.sectionIndex === guidance.activeSectionIndex,
                    );

                    return (
                      <motion.div
                        key={guidance.activeSectionIndex}
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="mt-3">
                        <h3 className="text-sm font-semibold text-gray-900">
                          {activeSection.title}
                        </h3>
                        <p className="mt-1 text-sm text-gray-600">
                          {activeSection.prompt}
                        </p>
                        {activeEntry?.evidence && (
                          <p className="mt-1 text-xs text-gray-500 italic">
                            {activeEntry.evidence}
                          </p>
                        )}
                      </motion.div>
                    );
                  })()}
                </AnimatePresence>
              </div>
            )}

            {/* Transcription display */}
            <div className="h-full min-h-0 flex-1">
              <TranscriptionDisplay
                reportId={report._id}
                isLoading={status === 'connecting'}
                isTranscribing={isTranscribing || isRecorderTransitioning}
                previousText={previousText}
                currentSegmentText={currentSegmentText}
                finalText={finalText}
                nonFinalText={nonFinalText}
                creationTime={report._creationTime}
              />
            </div>
          </div>
        )}

        {error && (
          <div className="flex w-full flex-col">
            {/* Error message */}
            <AnimatePresence>
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden">
                <div className="flex items-baseline justify-start gap-2 rounded-sm bg-red-500/10 p-4 text-red-500">
                  <AlertCircleIcon className="size-4" />
                  <div className="flex-1">
                    <p className="text-xl">
                      {t(`home:errors.${error?.status}.title`)}
                    </p>
                    <p className="text-base opacity-50">
                      {t(`home:errors.${error?.status}.description`, {
                        message: error?.message,
                      })}
                    </p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        )}
      </ResponsiveContainer>
      <ResponsiveContainer className="z-50 flex w-full flex-col items-center justify-between bg-white md:pb-4">
        <div className="w-full border-t pt-3 md:pt-4">
          <RecordingButton
            isTranscribing={isTranscribing}
            isSaving={isRecorderTransitioning}
            isLoading={isDeleting || isExtracting}
            transcriptionExists={transcriptionExists}
            startTranscription={startTranscription}
            stopTranscription={stopTranscription}
          />
          <AnimatePresence>
            {transcriptionExists && status === 'idle' && (
              <motion.div
                key="secondary-actions"
                variants={{
                  initial: { opacity: 0, height: 0 },
                  animate: {
                    opacity: 1,
                    height: 'auto',
                    transition: {
                      height: { duration: 0.2, ease: 'circOut' },
                      opacity: { duration: 0.2 },
                    },
                  },
                  exit: {
                    opacity: 0,
                    height: 0,
                    transition: {
                      height: { duration: 0.2, ease: 'circOut' },
                      opacity: { duration: 0.2 },
                    },
                  },
                }}
                initial="initial"
                animate="animate"
                exit="exit"
                className="w-full overflow-hidden">
                <div className="mt-3 flex w-full flex-row gap-3">
                  <div className="shrink-0 grow basis-0">
                    <Button
                      variant="destructive"
                      className="w-full"
                      onClick={handleDelete}
                      disabled={isDeleting || isExtracting}>
                      {isDeleting ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        <Trash2 className="size-4" />
                      )}
                      {t('home:delete')}
                    </Button>
                  </div>

                  <div className="shrink-0 grow basis-0">
                    <Button
                      className="w-full"
                      onClick={handleExtractData}
                      disabled={
                        isDeleting || isExtracting || !report.templateId
                      }>
                      {t('home:extractData')}
                      {isExtracting ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        <ArrowRight className="size-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </ResponsiveContainer>
    </>
  );
};
